///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Alert from "../../components/Alert";
import SystemErrorModal from "../../components/SystemErrorModal";
import Spinner from "../../components/icons/Spinner";
import AutoComplete from "../../components/form-controls/AutoComplete";
import { getEmployees } from "../../api/persons";
import { booleanTypes, divisionList, userRoleList } from "../../helpers/types";
import {
  getUserSetting,
  getUserSettingV2,
  putUserSetting
} from "../../api/user";
import messages from "../../helpers/messages";
import { fetchUser } from "../../slices/userSlice";
import { reset as resetPricingSlice } from "../../slices/bidPricingSlice";
import OutlinedInput from "../form-controls/OutlinedInput";

export const ManageUsers = () => {
  const dispatch = useDispatch();
  const loggedInUser = useSelector(state => state.user).user;
  const { enableInternalCapacityMatching, useRxoUserAdmin } =
    useSelector(state => state.featureFlags);

  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [division, setDivision] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [failureModal, setFailureModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null);
  const [seeAllActiveBids, setseeAllActiveBids] = useState(null);
  const [isRXOUser, setIsRXOUser] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [emailValidationError, setEmailValidationError] = useState(false);
  const [userRoles, setUserRoles] = useState(userRoleList);
  const [isFetching, setIsFetching] = useState(false);
  const [isUserSearched, setIsUserSearched] = useState(false);
  const [previousSearchedEmail, setPreviousSearchedEmail] = useState("");

  const handleUserChange = selectedUser => {
    setUser(selectedUser);
    if (selectedUser) {
      setRole(null);
      setDivision(null);
      setIsAdmin(null);
      setseeAllActiveBids(null);
      getUserSetting(selectedUser.value).then(({ data }) => {
        if (data) {
          setRole(userRoleList.find(t => t.value === data.userRole));
          setIsAdmin(booleanTypes.find(t => t.value === data.isAdmin));
          setseeAllActiveBids(
            booleanTypes.find(t => t.value === data.seeAllActiveBids)
          );
          if (divisionList.some(d => d.value === data.division)) {
            setDivision(divisionList.find(d => d.value === data.division));
          } else {
            setDivision(divisionList[0]);
          }
        } else {
          setRole(userRoleList[0]);
        }
      });
    }
  };

  const handleSearch = () => {
    if (email) {
      setRole(null);
      setDivision(null);
      setIsAdmin(null);
      setseeAllActiveBids(null);
      setFirstName("");
      setLastName("");
      setIsFetching(true);
      setIsUserSearched(false);
      setPreviousSearchedEmail(email);
      getUserSettingV2(email).then(({ data }) => {
        if (data) {
          setFirstName(data.firstName);
          setLastName(data.lastName);
          setUser({ userId: data.userId, value: data.userId });
          setRole(userRoleList.find(t => t.value === data.userRole));
          setIsAdmin(booleanTypes.find(t => t.value === data.isAdmin));
          setseeAllActiveBids(
            booleanTypes.find(t => t.value === data.seeAllActiveBids)
          );
          if (divisionList.some(d => d.value === data.division)) {
            setDivision(divisionList.find(d => d.value === data.division));
          } else {
            setDivision(divisionList[0]);
          }
        } else {
          setRole(userRoleList[0]);
        }
        setIsFetching(false);
        setIsUserSearched(true);
      });
    }
  };

  const saveChanges = () => {
    setFailureModal(false);
    setSaving(true);
    putUserSetting({
      userId: user?.value,
      userRole: role.value,
      division: division.value,
      isAdmin: isAdmin?.value,
      seeAllActiveBids: seeAllActiveBids?.value,
      isRxoUser: isRXOUser,
      firstName: isRXOUser ? firstName : null,
      lastName: isRXOUser ? lastName : null,
      email: isRXOUser ? email : null
    })
      .then(() => {
        setSaving(false);
        setSuccessModal(true);
      })
      .catch(() => {
        setSaving(false);
        setFailureModal(true);
      });
  };

  const handleSuccessModalClose = () => {
    if (user?.userId === loggedInUser.userId) {
      dispatch(fetchUser());
      dispatch(resetPricingSlice());
    } else {
      setSuccessModal(false);
      setRole(null);
      setUser(null);
      setDivision(null);
      setIsAdmin(null);
      setseeAllActiveBids(null);
      setFirstName("");
      setLastName("");
      setEmail("");
      setPreviousSearchedEmail("");
      setIsUserSearched(false);
    }
  };

  const handleEmailBlur = () => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (email && !emailRegex.test(email)) {
      setEmailValidationError(true);
    }
    if (email !== previousSearchedEmail) {
      setIsUserSearched(false);
    }
  };

  useEffect(() => {
    let userRolesOptions = userRoleList;

    if (!enableInternalCapacityMatching) {
      userRolesOptions = userRolesOptions.filter(
        item =>
          item.label !==
            messages.carrierRelationsRepresentative.defaultMessage &&
          item.label !== messages.procurementRepresentative.defaultMessage
      );
    }

    setUserRoles(userRolesOptions);
  }, [setUserRoles, enableInternalCapacityMatching]);

  return (
    <>
      <Alert
        title={messages.success.defaultMessage}
        message={messages.manageUsersSuccessMessage.defaultMessage}
        open={successModal}
        handleClose={handleSuccessModalClose}
        large={true}
      />
      <SystemErrorModal
        open={failureModal}
        handleTryAgain={saveChanges}
        closeModal={() => setFailureModal(false)}
        handleExit={() => setFailureModal(false)}
      />
      {useRxoUserAdmin ? (
        <div className="pl-24">
          <label>
            <input
              type="checkbox"
              checked={isRXOUser}
              onChange={({ target }) => setIsRXOUser(target.checked)}
            />
            <span className="align-text-bottom">
              {messages.isRXOUser.defaultMessage}
            </span>
          </label>
        </div>
      ) : null}
      <div className="py-16 grid section-grid">
        {!isRXOUser ? (
          <div
            data-testid="user-container"
            className="grid__cell one-half pl-24 pr-4 pb-12"
          >
            <AutoComplete
              handleOptionsMapping={options =>
                options.map(option => ({
                  ...option,
                  label: option.fullName,
                  value: option.userId
                }))
              }
              fetchFrom={getEmployees}
              label={messages.user.defaultMessage}
              value={user}
              handleChange={handleUserChange}
            />
          </div>
        ) : null}
        {isRXOUser ? (
          <>
            <div
              data-testid="email-container"
              className="grid__cell one-half pl-24 pr-4"
            >
              <OutlinedInput
                data-testid="email-name"
                value={email}
                onChange={event => setEmail(event.target.value)}
                label={messages.email.defaultMessage}
                onBlur={handleEmailBlur}
                onFocus={() => setEmailValidationError(false)}
                error={emailValidationError}
                validationMessage={messages.invalidEmailAddress.defaultMessage}
              />
            </div>
            <div
              data-testid="search-container"
              className="grid__cell one-half pl-4 pr-24"
            >
              <button
                className="secondary-button fl univers-67-bold-cond block"
                disabled={email.trim() && !emailValidationError ? false : true}
                onClick={handleSearch}
              >
                {isFetching && (
                  <Spinner
                    className="spinner"
                    fill="#69c764"
                    height="16px"
                    width="16px"
                  />
                )}
                {!isFetching && messages.search.defaultMessage}
              </button>
            </div>
            <div
              data-testid="first-name-container"
              className="grid__cell one-half pl-24 pr-4"
            >
              <OutlinedInput
                data-testid="first-name"
                value={firstName}
                onChange={event => setFirstName(event.target.value)}
                label={messages.firstName.defaultMessage}
                maxLength="100"
                disabled={!isUserSearched}
              />
            </div>
            <div
              data-testid="last-name-container"
              className="grid__cell one-half pl-4 pr-24"
            >
              <OutlinedInput
                data-testid="last-name"
                value={lastName}
                onChange={event => setLastName(event.target.value)}
                label={messages.lastName.defaultMessage}
                maxLength="100"
                disabled={!isUserSearched}
              />
            </div>
          </>
        ) : null}
        <div
          data-testid="user-role-container"
          className={`grid__cell one-half ${
            isRXOUser ? "pl-24 pr-4" : "pl-4 pr-24"
          }`}
        >
          <AutoComplete
            staticOptions={userRoles}
            label={messages.role.defaultMessage}
            onChange={setRole}
            value={role}
          />
        </div>
        <div
          data-testid="division-container"
          className={`grid__cell one-half ${
            isRXOUser ? "pl-4 pr-24" : "pl-24 pr-4"
          }`}
        >
          <AutoComplete
            staticOptions={divisionList}
            label={messages.division.defaultMessage}
            onChange={setDivision}
            value={division}
            backspaceRemovesValue={false}
          />
        </div>
        <div
          data-testid="is-admin-container"
          className={`grid__cell one-half ${
            isRXOUser ? "pl-24 pr-4" : "pl-4 pr-24"
          }`}
        >
          <AutoComplete
            staticOptions={booleanTypes}
            label={messages.adminUser.defaultMessage}
            onChange={setIsAdmin}
            value={isAdmin}
            backspaceRemovesValue={false}
          />
        </div>
        <div
          data-testid="seeAllActiveBids-container"
          className={`grid__cell one-half ${
            isRXOUser ? "pl-4 pr-24" : "pl-24 pr-4"
          }`}
        >
          <AutoComplete
            staticOptions={booleanTypes}
            label={messages.seeAllActiveBids.defaultMessage}
            onChange={setseeAllActiveBids}
            value={seeAllActiveBids}
            backspaceRemovesValue={false}
          />
        </div>

        <button
          data-testid="save"
          className="mt-40 mr-24 primary-button fr univers-67-bold-cond mt-12 mb-24 block"
          style={{ minWidth: "120px" }}
          disabled={
            (!isRXOUser && user && role) ||
            (isRXOUser &&
              firstName &&
              lastName &&
              email &&
              role &&
              division &&
              !emailValidationError &&
              isUserSearched)
              ? false
              : true
          }
          onClick={saveChanges}
        >
          {saving && (
            <Spinner
              className="spinner"
              fill="#ffffff"
              height="16px"
              width="16px"
            />
          )}
          {!saving && messages.save.defaultMessage}
        </button>
      </div>
    </>
  );
};

export default ManageUsers;
